import { fetchGql } from './fetch';
import type { GraphQLErrorResponse, GraphQLResponse } from './fetch/response';
import type { AssocMap, PersistedQuery } from './fetch/types';

type GqlPersistedQueryRequest<GqlResponseData> = (
  query?: string
) => Promise<GraphQLResponse<GqlResponseData>>;

interface TryPersistedQueryRequestArgs<GqlResponseData> {
  query: string;
  sendRequest: GqlPersistedQueryRequest<GqlResponseData>;
}

interface PrepareApqRequestArgs<GqlVariables> {
  preferGet: boolean;
  assocMap?: AssocMap;
  variables?: GqlVariables;
  persistedQuery: PersistedQuery;
}

interface ExecuteGqlPersistedQueryArgs<GqlVariables> {
  query: string;
  variables?: GqlVariables;
  assocMap?: AssocMap;
  persistedQuery: PersistedQuery;
}

const isPersistedQueryNotFoundError = (errors?: GraphQLErrorResponse[]) =>
  errors?.[0]?.extensions?.code === 'PERSISTED_QUERY_NOT_FOUND';

const isMutationOperation = (operation: string) => /mutation\s*(\w+)?\s*(\(|\{)/.test(operation);

const tryExecutePersistedQueryRequest = async <GqlResponseData>({
  query,
  sendRequest,
}: TryPersistedQueryRequestArgs<GqlResponseData>): Promise<GraphQLResponse<GqlResponseData>> => {
  const { data, errors } = await sendRequest();

  if (isPersistedQueryNotFoundError(errors)) {
    return sendRequest(query);
  }

  return { data, errors };
};

const preparePersistedQueryRequest =
  <GqlResponseData, GqlVariables>({
    preferGet,
    variables,
    assocMap,
    persistedQuery,
  }: PrepareApqRequestArgs<GqlVariables>) =>
  (query?: string) =>
    fetchGql<GqlResponseData, GqlVariables>({
      query,
      variables,
      assocMap,
      persistedQuery,
      preferGet,
    });

export const executeGqlPersistedQuery = <GqlResponseData, GqlVariables = unknown>({
  query,
  variables,
  assocMap,
  persistedQuery,
}: ExecuteGqlPersistedQueryArgs<GqlVariables>) => {
  const preferGet = !isMutationOperation(query);
  const sendRequest = preparePersistedQueryRequest<GqlResponseData, GqlVariables>({
    preferGet,
    variables,
    assocMap,
    persistedQuery,
  });

  return tryExecutePersistedQueryRequest({ query, sendRequest });
};
